import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { Navigate } from 'react-router-dom';
import {
  ChartBarIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  ServerIcon,
  CloudIcon,
  CpuChipIcon,
  CircleStackIcon,
  InformationCircleIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { Role } from '../../types';

// システムヘルス情報の型定義
interface SystemHealth {
  overall: 'healthy' | 'warning' | 'critical';
  lastUpdated: string;
  services: ServiceHealth[];
  metrics: SystemMetrics;
}

interface ServiceHealth {
  name: string;
  displayName: string;
  status: 'healthy' | 'warning' | 'critical' | 'down';
  responseTime?: number;
  uptime?: number;
  lastCheck: string;
  details?: string;
}

interface SystemMetrics {
  cpuUsage: number;
  memoryUsage: number;
  diskUsage: number;
  activeConnections: number;
  requestsPerMinute: number;
  errorRate: number;
}

const SystemHealthPage: React.FC = () => {
  const { user } = useAuth();
  const [systemHealth, setSystemHealth] = useState<SystemHealth | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const loadSystemHealth = async () => {
    // 仮のシステムヘルスデータ（実装準備中）
    const mockSystemHealth: SystemHealth = {
      overall: 'healthy',
      lastUpdated: new Date().toISOString(),
      services: [
        {
          name: 'supabase_auth',
          displayName: 'Supabase認証',
          status: 'healthy',
          responseTime: 45,
          uptime: 99.9,
          lastCheck: new Date().toISOString(),
        },
        {
          name: 'supabase_db',
          displayName: 'Supabaseデータベース',
          status: 'healthy',
          responseTime: 23,
          uptime: 99.8,
          lastCheck: new Date().toISOString(),
        },
        {
          name: 'supabase_storage',
          displayName: 'Supabaseストレージ',
          status: 'warning',
          responseTime: 156,
          uptime: 98.5,
          lastCheck: new Date().toISOString(),
          details: 'レスポンス時間が通常より高くなっています',
        },
        {
          name: 'gemini_ai',
          displayName: 'Google Gemini AI',
          status: 'healthy',
          responseTime: 892,
          uptime: 99.2,
          lastCheck: new Date().toISOString(),
        },
        {
          name: 'line_messaging',
          displayName: 'LINE Messaging API',
          status: 'critical',
          responseTime: 0,
          uptime: 95.1,
          lastCheck: new Date().toISOString(),
          details: 'API制限に達している可能性があります',
        },
        {
          name: 'google_maps',
          displayName: 'Google Maps API',
          status: 'healthy',
          responseTime: 78,
          uptime: 99.7,
          lastCheck: new Date().toISOString(),
        },
      ],
      metrics: {
        cpuUsage: 34.5,
        memoryUsage: 67.8,
        diskUsage: 23.4,
        activeConnections: 142,
        requestsPerMinute: 1250,
        errorRate: 0.12,
      },
    };

    await new Promise(resolve => setTimeout(resolve, 1000));
    setSystemHealth(mockSystemHealth);
  };

  useEffect(() => {
    loadSystemHealth().finally(() => setIsLoading(false));
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadSystemHealth();
    setRefreshing(false);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy':
        return 'text-green-600 bg-green-50';
      case 'warning':
        return 'text-yellow-600 bg-yellow-50';
      case 'critical':
        return 'text-red-600 bg-red-50';
      case 'down':
        return 'text-gray-600 bg-gray-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy':
        return <CheckCircleIcon className='h-4 w-4' />;
      case 'warning':
      case 'critical':
        return <ExclamationTriangleIcon className='h-4 w-4' />;
      case 'down':
        return <ServerIcon className='h-4 w-4' />;
      default:
        return <InformationCircleIcon className='h-4 w-4' />;
    }
  };

  const getStatusDisplayName = (status: string) => {
    switch (status) {
      case 'healthy':
        return '正常';
      case 'warning':
        return '注意';
      case 'critical':
        return '要対応';
      case 'down':
        return '停止';
      default:
        return '不明';
    }
  };

  // super_admin権限チェック
  if (!user || user.role !== Role.SUPER_ADMIN) {
    return <Navigate to='/' replace />;
  }

  const getMetricColor = (value: number, thresholds: { warning: number; critical: number }) => {
    if (value >= thresholds.critical) return 'text-red-600';
    if (value >= thresholds.warning) return 'text-yellow-600';
    return 'text-green-600';
  };

  if (isLoading) {
    return (
      <div className='min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center'>
        <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600'></div>
      </div>
    );
  }

  if (!systemHealth) {
    return (
      <div className='min-h-screen bg-gray-50 dark:bg-gray-900'>
        システムヘルス情報の取得に失敗しました
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-gray-50 dark:bg-gray-900'>
      {/* ヘッダー */}
      <div className='bg-white dark:bg-gray-800 shadow'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='flex justify-between items-center py-6'>
            <div>
              <h1 className='text-3xl font-bold text-gray-900 dark:text-white'>システム健全性</h1>
              <p className='mt-1 text-sm text-gray-500 dark:text-gray-400'>
                サービス監視とパフォーマンス指標の確認
              </p>
            </div>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className='inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50'
            >
              <ArrowPathIcon className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? '更新中...' : '最新情報に更新'}
            </button>
          </div>
        </div>
      </div>

      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
        {/* 実装準備中メッセージ */}
        <div className='mb-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4'>
          <div className='flex items-center'>
            <InformationCircleIcon className='h-5 w-5 text-blue-400 mr-2' />
            <p className='text-sm text-blue-800 dark:text-blue-200'>
              <strong>実装準備中:</strong>{' '}
              現在はサンプルデータを表示しています。実際のヘルスチェック機能はSprint
              3で実装予定です。
            </p>
          </div>
        </div>

        {/* 総合ステータス */}
        <div className='mb-8'>
          <div className='bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg'>
            <div className='p-6'>
              <div className='flex items-center justify-between'>
                <div className='flex items-center'>
                  <div className='flex-shrink-0'>{getStatusIcon(systemHealth.overall)}</div>
                  <div className='ml-4'>
                    <h3 className='text-lg font-medium text-gray-900 dark:text-white'>
                      システム総合ステータス
                    </h3>
                    <p className={`text-sm font-medium ${getStatusColor(systemHealth.overall)}`}>
                      {getStatusDisplayName(systemHealth.overall)}
                    </p>
                  </div>
                </div>
                <div className='text-right'>
                  <div className='flex items-center text-sm text-gray-500 dark:text-gray-400'>
                    <ClockIcon className='h-4 w-4 mr-1' />
                    最終更新: {new Date(systemHealth.lastUpdated).toLocaleString('ja-JP')}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* システム指標 */}
        <div className='mb-8'>
          <h2 className='text-xl font-semibold text-gray-900 dark:text-white mb-4'>システム指標</h2>
          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
            <div className='bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg'>
              <div className='p-5'>
                <div className='flex items-center'>
                  <div className='flex-shrink-0'>
                    <CpuChipIcon className='h-8 w-8 text-blue-600' />
                  </div>
                  <div className='ml-5 w-0 flex-1'>
                    <dl>
                      <dt className='text-sm font-medium text-gray-500 dark:text-gray-400 truncate'>
                        CPU使用率
                      </dt>
                      <dd
                        className={`text-lg font-medium ${getMetricColor(systemHealth.metrics.cpuUsage, { warning: 70, critical: 90 })}`}
                      >
                        {systemHealth.metrics.cpuUsage}%
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className='bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg'>
              <div className='p-5'>
                <div className='flex items-center'>
                  <div className='flex-shrink-0'>
                    <CircleStackIcon className='h-8 w-8 text-green-600' />
                  </div>
                  <div className='ml-5 w-0 flex-1'>
                    <dl>
                      <dt className='text-sm font-medium text-gray-500 dark:text-gray-400 truncate'>
                        メモリ使用率
                      </dt>
                      <dd
                        className={`text-lg font-medium ${getMetricColor(systemHealth.metrics.memoryUsage, { warning: 80, critical: 95 })}`}
                      >
                        {systemHealth.metrics.memoryUsage}%
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className='bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg'>
              <div className='p-5'>
                <div className='flex items-center'>
                  <div className='flex-shrink-0'>
                    <ServerIcon className='h-8 w-8 text-purple-600' />
                  </div>
                  <div className='ml-5 w-0 flex-1'>
                    <dl>
                      <dt className='text-sm font-medium text-gray-500 dark:text-gray-400 truncate'>
                        ディスク使用率
                      </dt>
                      <dd
                        className={`text-lg font-medium ${getMetricColor(systemHealth.metrics.diskUsage, { warning: 80, critical: 95 })}`}
                      >
                        {systemHealth.metrics.diskUsage}%
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className='bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg'>
              <div className='p-5'>
                <div className='flex items-center'>
                  <div className='flex-shrink-0'>
                    <ChartBarIcon className='h-8 w-8 text-indigo-600' />
                  </div>
                  <div className='ml-5 w-0 flex-1'>
                    <dl>
                      <dt className='text-sm font-medium text-gray-500 dark:text-gray-400 truncate'>
                        リクエスト/分
                      </dt>
                      <dd className='text-lg font-medium text-gray-900 dark:text-white'>
                        {systemHealth.metrics.requestsPerMinute.toLocaleString()}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className='bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg'>
              <div className='p-5'>
                <div className='flex items-center'>
                  <div className='flex-shrink-0'>
                    <ExclamationTriangleIcon className='h-8 w-8 text-yellow-600' />
                  </div>
                  <div className='ml-5 w-0 flex-1'>
                    <dl>
                      <dt className='text-sm font-medium text-gray-500 dark:text-gray-400 truncate'>
                        エラー率
                      </dt>
                      <dd
                        className={`text-lg font-medium ${getMetricColor(systemHealth.metrics.errorRate, { warning: 1, critical: 5 })}`}
                      >
                        {systemHealth.metrics.errorRate}%
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className='bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg'>
              <div className='p-5'>
                <div className='flex items-center'>
                  <div className='flex-shrink-0'>
                    <CloudIcon className='h-8 w-8 text-cyan-600' />
                  </div>
                  <div className='ml-5 w-0 flex-1'>
                    <dl>
                      <dt className='text-sm font-medium text-gray-500 dark:text-gray-400 truncate'>
                        アクティブ接続数
                      </dt>
                      <dd className='text-lg font-medium text-gray-900 dark:text-white'>
                        {systemHealth.metrics.activeConnections}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* サービス別ヘルス */}
        <div>
          <h2 className='text-xl font-semibold text-gray-900 dark:text-white mb-4'>
            サービス別ヘルス
          </h2>
          <div className='bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden'>
            <div className='overflow-x-auto'>
              <table className='min-w-full divide-y divide-gray-200 dark:divide-gray-700'>
                <thead className='bg-gray-50 dark:bg-gray-700'>
                  <tr>
                    <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider'>
                      サービス
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider'>
                      ステータス
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider'>
                      レスポンス時間
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider'>
                      稼働率
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider'>
                      最終チェック
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider'>
                      詳細
                    </th>
                  </tr>
                </thead>
                <tbody className='bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700'>
                  {systemHealth.services.map(service => (
                    <tr key={service.name} className='hover:bg-gray-50 dark:hover:bg-gray-700'>
                      <td className='px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white'>
                        {service.displayName}
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap'>
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(service.status)}`}
                        >
                          {getStatusIcon(service.status)}
                          <span className='ml-1'>{getStatusDisplayName(service.status)}</span>
                        </span>
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white'>
                        {service.responseTime ? `${service.responseTime}ms` : 'N/A'}
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white'>
                        {service.uptime ? `${service.uptime}%` : 'N/A'}
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400'>
                        {new Date(service.lastCheck).toLocaleString('ja-JP')}
                      </td>
                      <td className='px-6 py-4 text-sm text-gray-900 dark:text-white'>
                        {service.details || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SystemHealthPage;
